import { Component, Input } from '@angular/core';
import * as Highcharts from 'highcharts';
import { catchError, forkJoin, map, Observable, of, Subscription, tap } from 'rxjs';
import { timeseriesChartXaxis, reportChartLineOptions } from '../highcharts-options';
import {
  PositionStatus,
  QuickBuild,
  QuickBuildService,
  UIFilter,
  UniqueIdGenerator,
} from 'src/app/people/quick-build.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import * as moment from 'moment';
import { getPeopleColumnByName } from 'src/app/people/people-columns';
import {
  BaseReportComponent,
  ReportResponseWithName,
  ChartContextMenuItem,
} from '../base-report.component';
import { Log } from 'src/app/logger.service';
import { SearchFilter } from 'ldt-people-api';
import { StreamsSubscribeDrawerService } from '../../streams-subscribe-drawer/streams-subscribe-drawer.service';
import * as dayjs from 'dayjs';
import { ToggleOption } from '../../components/option-toggle/option-toggle.component';
import { HistoricalTenureComparisonConfig } from '../interfaces/report-config.interfaces';
import { AuthService } from 'src/app/auth/service/auth.service';
import { StatsigService } from '@statsig/angular-bindings';
import { WorkforceLayoutService } from 'src/app/workforce/workforce-layout.service';

const REPORT_NAME = 'tenure';

@Component({
  selector: 'app-reports-historical-tenure-comparison',
  templateUrl: './historical-tenure-comparison.component.html',
  styleUrls: ['./historical-tenure-comparison.component.scss'],
  standalone: false,
})
export class HistoricalTenureComparisonComponent extends BaseReportComponent<HistoricalTenureComparisonConfig> {
  @Input() quickBuilds: QuickBuild[];
  @Input() dateFrom: string;
  @Input() dateTo: string;
  @Input() boxShadow: boolean = true;

  protected readonly CHART_ID = 'historical-tenure-comparison';
  protected readonly CHART_DATA_DESC = 'Historical tenure over time by company';

  chartId: string;
  subscriptions: Subscription = new Subscription();

  tenureTypeOptions: ToggleOption[] = [
    { value: 'active', displayText: 'Active' },
    { value: 'departed', displayText: 'Departed' },
  ];

  @Input()
  set selectedTenureType(value: string | undefined) {
    if (value) {
      this._selectedTenureType = value;
    }
  }
  get selectedTenureType(): string {
    return this._selectedTenureType;
  }

  private _selectedTenureType: string = 'departed';

  constructor(
    reportService: PeopleReportService,
    quickBuildService: QuickBuildService,
    streamsDrawerService: StreamsSubscribeDrawerService,
    auth: AuthService,
    statsigService: StatsigService,
    layoutService: WorkforceLayoutService
  ) {
    super(
      reportService,
      quickBuildService,
      streamsDrawerService,
      auth,
      statsigService,
      layoutService
    );
    this.chartId = UniqueIdGenerator.generate();
    if (!this.dateFrom || !this.dateTo) {
      this.dateFrom = moment().subtract(2, 'year').startOf('month').format('YYYY-MM');
      this.dateTo = moment().startOf('month').format('YYYY-MM');
    }

    // Register configuration properties for automatic emission
    this.registerConfigProperty('selectedTenureType', () => this._selectedTenureType);
  }

  protected initializeChart(): void {
    this.chart = Highcharts.chart(`highchartsContainer-${this.chartId}`, reportChartLineOptions);
    this.chart.update(HistoricalTenureComparisonChartOptions, true, true);
    Log.d('HistoricalTenureComparisonComponent initializeChart', this.chart);
    this.initializeContextMenu();
  }

  protected getChartData(): void {
    Log.d(
      'HistoricalTenureComparisonComponent getChartData',
      this.quickBuilds,
      this.dateFrom,
      this.dateTo
    );
    if (!this.quickBuilds) return;
    // Set this primarily for the chart subtitles to generate in base-report
    this.quickBuild = this.quickBuilds[0];

    this.showLoading();

    // Active tenure doesn't support job_role filters
    const fGroup = QuickBuildService.findFilterGroupById(
      'moneyball',
      this.quickBuild?.rootFilterGroup
    );
    if (
      fGroup?.filters.some(
        (f) => QuickBuildService.isUIFilter(f) && f.filter.field === 'job_role'
      ) &&
      this._selectedTenureType === 'active'
    ) {
      this.showChartError(undefined, "Active tenure doesn't support role filters");
      return;
    }

    // Get the data for each quick build (Company), joining together so we can provide the collected data to the AI
    this.subscriptions.add(
      forkJoin(
        this.quickBuilds
          .filter((quickBuild) => quickBuild)
          .map((quickBuild) => {
            return this.addQuickbuildToChart(quickBuild);
          })
      ).subscribe({
        next: (data: (ReportResponseWithName | null)[]) => {
          Log.d('HistoricalTenureComparisonComponent getChartData next');
          this.chartLoadingComplete();
        },
        error: (error) => {
          Log.e('HistoricalTenureComparisonComponent getChartData error', error);
          this.showChartError(error);
        },
      })
    );
  }

  // Add a single company to the chart
  addQuickbuildToChart(quickBuild: QuickBuild): Observable<ReportResponseWithName | null> {
    Log.d(
      'HistoricalTenureComparisonComponent addQuickbuildToChart',
      quickBuild,
      this.dateFrom,
      this.dateTo
    );

    // Remove the company from the chart if it already exists
    this.removeQuickbuildFromChart(quickBuild.id, quickBuild.name);

    // We need the arrivals/departures, and for pct values we also need the total counts
    const reportParams = {
      date_from: this.dateFrom,
      date_to: this.dateTo,
      status: this._selectedTenureType,
    };
    const tenureReport = this.reportService.getReportDataWithReportDetails(
      this.reportId,
      REPORT_NAME,
      reportParams,
      this.reportTarget,
      quickBuild
    );

    // Get both datasets and generate the chart series
    const sub = tenureReport.pipe(
      tap((tenureData) => {
        this.generateChartSeries(quickBuild.id, quickBuild.name, tenureData.data.tenure);
      }),
      map((data) => ({
        name: quickBuild.name,
        data: data,
      })),
      catchError((error) => {
        Log.e('HistoricalTenureComparisonComponent addQuickbuildToChart error', error);
        throw error;
      })
    );

    return sub;
  }

  async resetChartData() {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    await this.removeAllSeriesFromChart();
  }

  // Generate the chart series for a single company given the required data
  generateChartSeries(quickBuildId: string, quickBuildName: string, tenureData: any): void {
    Log.d('HistoricalTenureComparisonComponent generateChartSeries', tenureData);

    this.addSeriesToChart({
      custom: { id: quickBuildId },
      id: quickBuildId,
      name: quickBuildName,
      data: tenureData.map((d: any) => [new Date(d.date).valueOf(), d.avg_tenure]),
      type: 'spline',
      colorIndex: this.quickBuilds.findIndex((qb) => qb.id === quickBuildId),
    });
  }

  removeQuickbuildFromChart(quickBuildId: string, quickBuildName: string) {
    if (!this.chart) return;
    this.chart.series.forEach((series) => {
      if (series.options.custom?.id === quickBuildId) {
        series.remove();
      }
    });
  }

  protected getContextMenuItems(point: Highcharts.Point): ChartContextMenuItem[] {
    const date = dayjs(point.x as number);
    const series = point.series;
    const quickBuildId = series.options.custom?.id;
    const quickBuild = this.quickBuilds.find((qb) => qb.id === quickBuildId);
    if (!quickBuild) {
      Log.s('HistoricalTenureComparisonComponent getContextMenuItems', 'error', {
        error: 'No quick build found',
        seriesCustomOptions: series.options.custom ? JSON.stringify(series.options.custom) : '',
        quickBuilds: JSON.stringify(this.quickBuilds),
      });
      return [];
    }

    Log.d('HistoricalTenureComparisonComponent getContextMenuItems', point);

    const qbCopy = QuickBuildService.copyQuickBuild(quickBuild);

    const endDateFilter: UIFilter = {
      filter: {
        field: 'jobs.ended_at',
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        date_from: date.toISOString(),
        date_to: date.toISOString(),
      },
    };
    const activeAtFilter: UIFilter = {
      filter: {
        field: 'job_active_at',
        match_type: SearchFilter.MatchTypeEnum.Fuzzy,
        type: SearchFilter.TypeEnum.Must,
        date_from: date.toISOString(),
      },
    };

    const items: ChartContextMenuItem[] = [];

    if (this._selectedTenureType === 'departed') {
      items.push({
        label: `View People that departed ${series.name} in ${date.utc().format('MMM YYYY')}`,
        icon: 'calendar_month',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(qbCopy, [endDateFilter], 'moneyball');
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          group!.positionStatus = PositionStatus.Last;
          qbCopy.name = `People that departed ${series.name} in ${date.utc().format('MMM YYYY')}`;
          this.openDrawer(qbCopy);
        },
      });
    } else {
      items.push({
        label: `View People that worked at ${series.name} in ${date.utc().format('MMM YYYY')}`,
        icon: 'calendar_month',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(qbCopy, [activeAtFilter], 'moneyball');
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          qbCopy.name = `People that worked at ${series.name} in ${date.utc().format('MMM YYYY')}`;
          this.openDrawer(qbCopy);
        },
      });
    }

    return items;
  }

  toggleTenureType(value: string) {
    if (!value) return;

    this._selectedTenureType = value;
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }

  getFieldDisplayName(field: string): string {
    return getPeopleColumnByName(field).displayName;
  }
}

const HistoricalTenureComparisonChartOptions: Highcharts.Options = {
  title: {
    text: 'Employee Tenure',
  },
  xAxis: timeseriesChartXaxis,
  yAxis: [
    {
      title: {
        text: 'Avg Tenure (months)',
      },
    },
  ],
};
